import React, { useState } from "react";
import { Box, Typography, Stack, Container } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CommonTable1 from "app/components/Tables/CommonTable1";
import AddIcon from "@mui/icons-material/Add";
import TableActionButtons from "app/components/common/TableActionButtons";
import AddDocumentDialog from "./Dialogs/AddDocumentDialog";
import SendReminderDialog from "./Dialogs/SendReminderDialog";
import { useQuery, useMutation } from "react-query";
import {
  getRequiredDocuments,
  deleteRequiredDocument,
} from "app/services/student-management";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  selectSelectedSchoolId,
  selectActiveSession,
} from "app/modules/schools/selectors";
import DeleteDialog from "app/components/ConfirmationDialog/DeleteDialog";

const RequiredDocument = ({ processingDocumentId, handleSendReminder }) => {
  const schoolDocId = useSelector(selectSelectedSchoolId);
  const selectedSession = useSelector(selectActiveSession);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    documentId: null,
  });

  const { data, isLoading, isFetching, refetch, error } = useQuery(
    ["getRequiredDocuments", schoolDocId, selectedSession],
    () => {
      console.log("Fetching documents with:", { schoolDocId, selectedSession });
      return getRequiredDocuments({ schoolDocId, selectedSession });
    },
    {
      select: (res) => {
        console.log("API Response:", res);
        return res?.data?.list || [];
      },
      enabled: Boolean(schoolDocId && selectedSession), 
      retry: 2,
      staleTime: 0, 
      cacheTime: 0, 
      onError: (error) => {
        console.error("Error fetching documents:", error);
        toast.error(
          error?.response?.data?.message || "Failed to fetch documents"
        );
      },
    }
  );
  const [documentDialogState, setDocumentDialogState] = useState({
    open: false,
    edit: false,
    editData: {},
  });
  
    // const [reminderDialog, setReminderDialog] = useState({
    //   open: false,
    //   documentId: null,
    // });
    // *Open/close reminder dialog*
    // const handleOpenReminderDialog = (documentId) => {
    //   setReminderDialog({ open: true, documentId });
    // };
    // const handleCloseReminderDialog = () => {
    //   setReminderDialog({ open: false, documentId: null });
    // };
    
  // Delete mutation
  const { mutate: deleteDocument, loading: deleteLoading } = useMutation(
    (documentId) => deleteRequiredDocument(documentId),
    {
      onSuccess: () => {
        toast.success("Document deleted successfully");
        refetch();
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || "Failed to delete document"
        );
      },
    }
  );

  // Close dialog handler
  const handleCloseDialog = () => {
    setDocumentDialogState((prev) => ({
      ...prev,
      open: false,
      edit: false,
      editData: {},
    }));
  };

  // Edit document handler
  const handleEditDocument = (documentData) => {
    setDocumentDialogState({
      open: true,
      edit: true,
      editData: documentData,
    });
  };

  const columns = [
    {
      header: "Sr No.",
      id: "srNo",
      Cell: ({ row }) => {
        const num = row.index + 1;
        return <Typography>{num.toString().padStart(2, "0")}</Typography>;
      },
      size: 20,
    },
    {
      header: "Document Name",
      accessorKey: "documentName",
      Cell: ({ cell }) => <Typography>{cell.getValue()}</Typography>,
      size: 50,
    },
    {
      header: "Document Code",
      accessorKey: "documentCode",
      Cell: ({ cell }) => <Typography>{cell.getValue() || "NA"}</Typography>,
      size: 40,
    },
    {
      header: "Applied On Student",
      accessorKey: "studentCount",
      Cell: ({ cell }) => <Typography>{cell.getValue()}</Typography>,
      size: 50,
    },
    {
      header: "Hard Copy Received",
      accessorKey: "receivedDocumentCount",
      Cell: ({ cell }) => <Typography>{cell.getValue()}</Typography>,
      size: 50,
    },
    {
      header: "Uploaded",
      accessorKey: "uploadedCount",
      Cell: ({ cell }) => <Typography>{cell.getValue()}</Typography>,
      size: 50,
    },
    {
      header: "Actions",
      Cell: ({ row }) => (
        <Stack direction="row" gap={1} alignItems="center">
          <TableActionButtons
            showEdit={true}
            showDelete={true}
            handleDelete={() => {
              setDeleteDialog({
                open: true,
                documentId: row.original.documentsettingDocId,
              });
            }}
            handleEdit={() => handleEditDocument(row.original)}
          />
          {/* <button
            onClick={() => handleOpenReminderDialog(row.original.id)}
            style={{
              backgroundColor: "#EBF0F4",
              borderRadius: "8px",
              border: "none",
              outline: "none",
              width: "2.5rem",
              height: "2.5rem",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Send Reminder"
          >
            📧
          </button> */}
        </Stack>
      ),
      maxSize: 80,
    },
  ];

  return (
    <>
      <Container maxWidth="xl">
        <Stack
          direction="row"
          sx={{ mt: "2rem", justifyContent: "space-between" }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              alignSelf: "center",
            }}
          >
            Total Required Documents:{" "}
            <span style={{ fontSize: "18px" }}>{data?.length || 0}</span>
          </Typography>
          <LoadingButton
            variant="outlined"
            color="secondary"
            size="large"
            startIcon={<AddIcon />}
            sx={{
              textTransform: "none",
              borderRadius: "6px",
              padding: "8px 16px",
            }}
            onClick={() =>
              setDocumentDialogState((prev) => ({ ...prev, open: true }))
            }
          >
            ADD DOCUMENT
          </LoadingButton>
        </Stack>

        <Box sx={{ mt: "1rem" }}>
          <CommonTable1
            columns={columns}
            data={data}
            maxHeight="60vh"
            isLoading={isLoading || isFetching}
          />
        </Box>
      </Container>

      {/* Add Document Dialog */}
      <AddDocumentDialog
        open={documentDialogState.open}
        edit={documentDialogState.edit}
        editData={documentDialogState.editData}
        onClose={handleCloseDialog}
        refetch={() => {
          refetch();
          handleCloseDialog();
        }}
      />
      {/* Send Reminder Dialog */}
      {/* <SendReminderDialog
        open={reminderDialog.open}
        documentId={reminderDialog.documentId}
        onClose={handleCloseReminderDialog}
      /> */}
      <DeleteDialog
        open={deleteDialog.open}
        handleClose={() => setDeleteDialog({ open: false, documentId: null })}
        handleDelete={() => {
          if (deleteDialog.documentId) {
            deleteDocument(deleteDialog.documentId);
          }
          setDeleteDialog({ open: false, documentId: null });
        }}
        loading={deleteLoading}
      />
    </>
  );
};

export default RequiredDocument;
